import React from "react";
import { Box } from "@sproutsocial/seeds-react-box";
import { Button } from "@sproutsocial/seeds-react-button";
import { Icon } from "@sproutsocial/seeds-react-icon";
import {
  ActionMenu,
  MenuContent,
  MenuItem,
  MenuGroup,
  MenuToggleButton,
} from "@sproutsocial/seeds-react-menu";
import {
  CollapsibleContentHeader,
  type TypeHeadingLevel,
} from "@sproutsocial/seeds-react-content-header";
import * as RadixAccordion from "@radix-ui/react-accordion";
import {
  type TypeRelatedAction,
  type TypeOverflowMenuConfig,
  type TypeAccordionSystemProps,
} from "./AccordionTypes";

const MAX_RELATED_ACTIONS = 2;

// Wrapper to adapt RadixAccordion.Trigger to the ContentHeader trigger interface
const AccordionTriggerWrapper = ({
  children,
}: {
  children: React.ReactElement;
}) => <RadixAccordion.Trigger asChild>{children}</RadixAccordion.Trigger>;

export interface TypeAccordionTriggerInnerProps {
  title: string;
  headingLevel: TypeHeadingLevel;
  leftSlot?: React.ReactNode;
  rightSlot?: React.ReactNode;
  relatedActions?: TypeRelatedAction[];
  overflowMenu?: TypeOverflowMenuConfig;
  color?: TypeAccordionSystemProps["color"];
  triggerIcon: React.ReactElement | null;
  triggerPosition: string;
}

/**
 * Wrapper-agnostic inner subtree of the accordion trigger (overflow menu,
 * related actions, and CollapsibleContentHeader). Shared by both the
 * styled-components and Tailwind trigger wrappers so they render an identical
 * subtree and guarantee visual parity.
 */
export const AccordionTriggerInner = ({
  title,
  headingLevel,
  leftSlot,
  rightSlot,
  relatedActions,
  overflowMenu,
  color,
  triggerIcon,
  triggerPosition,
}: TypeAccordionTriggerInnerProps) => {
  // Validate and limit related actions
  const validatedActions = relatedActions?.slice(0, MAX_RELATED_ACTIONS);

  const shouldRenderActionsBlock = Boolean(overflowMenu || validatedActions);

  // Render overflow menu from config
  const renderedOverflowMenu = overflowMenu && (
    <ActionMenu
      menuToggleElement={
        <MenuToggleButton
          aria-label={overflowMenu["aria-label"]}
          appearance="unstyled"
          color={color}
        >
          <Icon name="ellipsis-horizontal-outline" aria-hidden="true" />
        </MenuToggleButton>
      }
    >
      <MenuContent>
        <MenuGroup id="overflow-actions">
          {overflowMenu.items.map((item, index) => {
            const { iconName, id, onClick, children, ...menuItemProps } = item;
            return (
              <MenuItem
                key={id || `overflow-item-${index}`}
                id={id || `overflow-item-${index}`}
                onClick={onClick}
                {...menuItemProps}
              >
                {iconName ? (
                  <Box
                    display="flex"
                    alignItems="center"
                    gap="300"
                    color={color}
                  >
                    <Icon name={iconName} />
                    {children}
                  </Box>
                ) : (
                  children
                )}
              </MenuItem>
            );
          })}
        </MenuGroup>
      </MenuContent>
    </ActionMenu>
  );

  // Render related actions from config
  const renderedRelatedActions = validatedActions &&
    validatedActions.length > 0 && (
      <Box display="flex">
        {validatedActions.map((action, index) => (
          <Button
            key={`${action.iconName}-${index}`}
            onClick={action.onClick}
            aria-label={action["aria-label"]}
            color={color}
          >
            <Icon name={action.iconName} aria-hidden="true" />
          </Button>
        ))}
      </Box>
    );

  // Compose actions into a single ReactNode for ContentHeader
  const renderedActions = shouldRenderActionsBlock ? (
    <Box mr={300} display="flex">
      {renderedOverflowMenu}
      {renderedRelatedActions}
    </Box>
  ) : undefined;

  return (
    <CollapsibleContentHeader
      title={title}
      headingLevel={headingLevel}
      leftSlot={leftSlot}
      rightSlot={rightSlot}
      headerActions={renderedActions}
      trigger={AccordionTriggerWrapper}
      triggerPosition={triggerPosition as "left" | "right"}
      triggerIcon={triggerIcon}
      bordered={false}
    />
  );
};
